"use client";

import { useEffect, useState } from "react";
import { buildDocument } from "@/lib/invoice/document";
import { tint } from "@/lib/invoice/templates";

function useQr(text) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let alive = true;
    if (!text) {
      setSrc("");
      return;
    }
    import("qrcode").then((QR) => QR.toDataURL(text, { margin: 1, width: 240 }).then((u) => alive && setSrc(u)));
    return () => {
      alive = false;
    };
  }, [text]);
  return src;
}

const Lines = ({ lines, className, align }) =>
  lines.map((l) => (
    <div key={l} className={className} style={align ? { textAlign: align } : undefined}>
      {l}
    </div>
  ));

// On-screen A4 rendering of an invoice; mirrors pdf.js for every template.
export default function InvoicePreview({ invoice, data, thumbnail }) {
  const d = buildDocument(invoice, data);
  const qr = useQr(thumbnail ? "" : d.qr);
  const tpl = d.template;
  const accent = d.accent;
  const soft = tint(accent, 0.88);
  const serif = tpl.font === "times";
  const dense = tpl.density < 1;
  const cell = tpl.bold ? "px-2 py-2.5" : dense ? "px-2 py-[3px]" : "px-2 py-1.5";
  const num = tpl.bold ? "text-left" : "text-right";
  // bold classic: white dividers between the red header cells
  const th = (extra) => `px-2 ${tpl.bold ? "py-3 border-x border-white/80" : "py-2.5"} ${extra}`;

  const headStyle = {
    fill: { background: accent, color: "#fff" },
    tint: { background: soft, color: accent },
    dark: { background: "#1f2937", color: "#fff" },
    line: { borderBottom: `2px solid ${accent}`, color: "#1e1e1e" },
  }[tpl.head];
  const footStyle = tpl.head === "fill" ? { background: accent, color: "#fff" } : tpl.head === "dark" ? { background: "#1f2937", color: "#fff" } : { borderTop: "1.5px solid #1e1e1e" };
  const bodyBorder = tpl.grid ? "border border-neutral-200" : "border-b border-neutral-200";
  const cols = 6 + (d.showHsn ? 1 : 0) + (d.showDiscount ? 1 : 0);

  const clientLogo = d.billTo.logo && <img src={d.billTo.logo} alt="" className="h-11 w-11 shrink-0 object-contain" />;
  const copy = d.copyLabel && <div className="rounded border border-neutral-400 px-3 py-1 text-[10px] font-bold">{d.copyLabel}</div>;

  let header;
  if (tpl.header === "executive") {
    header = (
      <>
        <div className="-mx-6 -mt-6 px-6 pb-6 pt-7 text-white sm:-mx-10 sm:-mt-10 sm:px-10" style={{ background: "#111827" }}>
          <div className="flex items-start justify-between gap-6">
            <div className="flex items-center gap-4">
              {d.company.logo && (
                <div className="rounded-xl bg-white p-2">
                  <img src={d.company.logo} alt="" className="h-14 w-14 object-contain" />
                </div>
              )}
              <div>
                <div className="text-[24px] font-semibold leading-tight tracking-tight">{d.company.name}</div>
                {d.company.tagline && (
                  <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: tint(accent, 0.45) }}>
                    {d.company.tagline}
                  </div>
                )}
                <Lines lines={d.company.lines} className="text-[10.5px] text-white/70" />
              </div>
            </div>
            <div className="shrink-0 text-right">
              <div className="text-[22px] font-light tracking-[0.25em]" style={{ color: tint(accent, 0.45) }}>
                {d.title}
              </div>
              {d.copyLabel && <div className="mt-1 text-[9.5px] tracking-wider text-white/60">{d.copyLabel}</div>}
            </div>
          </div>
        </div>
        <div className="-mx-6 h-1.5 sm:-mx-10" style={{ background: accent }} />
        <div className="mt-5 grid gap-3" style={{ gridTemplateColumns: `repeat(${d.meta.length}, minmax(0,1fr))` }}>
          {d.meta.map((m) => (
            <div key={m.label} className="border-l-2 pl-3" style={{ borderColor: accent }}>
              <div className="text-[9px] uppercase tracking-[0.15em] text-neutral-500">{m.label.replace(":", "")}</div>
              <div className="text-[12.5px] font-bold">{m.value}</div>
            </div>
          ))}
        </div>
      </>
    );
  } else if (tpl.header === "boxed") {
    const cells = [...d.meta, ...d.extraMeta];
    const lastRow = Math.ceil(cells.length / 2) - 1;
    header = (
      <>
        <div className="flex items-center justify-between gap-3">
          <div className="w-1/4" />
          <div className="text-[15px] font-bold tracking-[0.35em]" style={{ color: accent }}>
            {d.title}
          </div>
          <div className="w-1/4 text-right text-[9.5px] font-semibold tracking-wider text-neutral-500">{d.copyLabel}</div>
        </div>
        <div className="mt-2 grid grid-cols-[1.2fr_1fr] border border-neutral-300">
          <div className="flex gap-3 border-r border-neutral-300 p-3">
            {d.company.logo && <img src={d.company.logo} alt="" className="h-16 w-16 shrink-0 object-contain" />}
            <div>
              <div className="text-[14px] font-bold">{d.company.name}</div>
              <Lines lines={d.company.lines} className="text-[10.5px] text-neutral-700" />
            </div>
          </div>
          <div className="grid grid-cols-2 content-start">
            {cells.map((m, i) => (
              <div key={m.label} className={`px-3 py-2 ${i % 2 === 0 ? "border-r border-neutral-300" : ""} ${Math.floor(i / 2) < lastRow ? "border-b border-neutral-300" : ""}`}>
                <div className="text-[9.5px] text-neutral-500">{m.label.replace(":", "")}</div>
                <div className="font-bold">{m.value}</div>
              </div>
            ))}
          </div>
          <div className="col-span-2 flex justify-between gap-3 border-t border-neutral-300 p-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: accent }}>
                Bill to
              </div>
              <div className="mt-0.5 font-bold">{d.billTo.name}</div>
              <Lines lines={d.billTo.lines} className="text-neutral-700" />
            </div>
            {clientLogo}
          </div>
        </div>
      </>
    );
  } else if (tpl.header === "banner") {
    const cards = [...d.meta.map((m) => [m.label.replace(":", ""), m.value]), [d.totals.balance < d.totals.total ? "Balance Due" : "Amount", d.summary.find((s) => s.tone === "total")?.value]];
    header = (
      <>
        <div className="-mx-6 -mt-6 flex items-center justify-between gap-4 px-6 py-6 text-white sm:-mx-10 sm:-mt-10 sm:px-10" style={{ background: accent }}>
          <div className="flex items-center gap-4">
            {d.company.logo && (
              <div className="rounded-lg bg-white p-1.5">
                <img src={d.company.logo} alt="" className="h-12 w-12 object-contain" />
              </div>
            )}
            <div>
              <div className="text-[22px] font-bold leading-tight">{d.company.name}</div>
              <Lines lines={d.company.lines.slice(0, 4)} className="text-[10.5px] opacity-90" />
            </div>
          </div>
          <div className="text-right">
            <div className="text-[26px] font-bold leading-none">{d.title}</div>
            {d.copyLabel && <div className="mt-2 text-[10px] opacity-90">{d.copyLabel}</div>}
          </div>
        </div>
        <div className="mt-5 grid gap-2" style={{ gridTemplateColumns: `repeat(${cards.length}, minmax(0,1fr))` }}>
          {cards.map(([l, v], i) => (
            <div key={l} className="rounded-md px-3 py-2" style={{ background: soft }}>
              <div className="text-[9px] uppercase tracking-wide text-neutral-500">{l}</div>
              <div className="text-[12.5px] font-bold" style={i === cards.length - 1 ? { color: accent } : undefined}>
                {v}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  } else if (tpl.header === "minimal") {
    header = (
      <>
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="text-[30px] font-light leading-none">{d.title.charAt(0) + d.title.slice(1).toLowerCase()}</div>
            {d.copyLabel && <div className="mt-2 text-[10px] text-neutral-500">{d.copyLabel}</div>}
          </div>
          <div className="text-right">
            {d.company.logo && <img src={d.company.logo} alt="" className="mb-1 ml-auto h-12 w-12 object-contain" />}
            <div className="text-[13px] font-bold">{d.company.name}</div>
            <Lines lines={d.company.lines} className="text-[10.5px] text-neutral-600" />
          </div>
        </div>
        <div className="mt-4 border-t border-neutral-800" />
        <div className="mt-4 flex flex-wrap gap-x-12 gap-y-2">
          {d.meta.map((m) => (
            <div key={m.label}>
              <div className="text-[9px] uppercase tracking-wide text-neutral-500">{m.label.replace(":", "")}</div>
              <div className="font-bold">{m.value}</div>
            </div>
          ))}
        </div>
      </>
    );
  } else if (tpl.header === "split") {
    header = (
      <>
        <div className="-mx-6 -mt-6 mb-5 h-2 sm:-mx-10 sm:-mt-10" style={{ background: accent }} />
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            {d.company.logo && <img src={d.company.logo} alt="" className="h-14 w-14 object-contain" />}
            <div>
              <div className="text-[20px] font-bold text-slate-800">{d.company.name}</div>
              <Lines lines={d.company.lines} className="text-[10.5px] text-neutral-600" />
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="text-[20px] font-bold" style={{ color: accent }}>
              {d.title}
            </div>
            {copy}
          </div>
        </div>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="border border-neutral-200">
            <div className="bg-gray-100 px-3 py-1.5 text-[10px] font-bold text-slate-700">BILL TO</div>
            <div className="flex justify-between gap-2 px-3 py-2">
              <div>
                <div className="font-bold">{d.billTo.name}</div>
                <Lines lines={d.billTo.lines} className="text-neutral-600" />
              </div>
              {clientLogo}
            </div>
          </div>
          <div className="border border-neutral-200">
            <div className="bg-gray-100 px-3 py-1.5 text-[10px] font-bold text-slate-700">{d.noun.toUpperCase()} DETAILS</div>
            <table className="m-2 w-[calc(100%-1rem)]">
              <tbody>
                {[...d.meta, ...d.extraMeta].map((m) => (
                  <tr key={m.label}>
                    <td className="py-0.5 pr-3 font-bold">{m.label.replace(":", "")}</td>
                    <td className="py-0.5 text-right text-neutral-600">{m.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  } else if (tpl.header === "centered") {
    header = (
      <div className="text-center">
        {d.company.logo && <img src={d.company.logo} alt="" className="mx-auto mb-2 h-14 w-14 object-contain" />}
        <div className="text-[26px] font-bold" style={{ color: accent }}>
          {d.company.name}
        </div>
        {d.company.tagline && <div className="italic text-neutral-500">{d.company.tagline}</div>}
        <Lines lines={d.company.lines} className="text-[10.5px] text-neutral-600" />
        <div className="mt-3 border-t-2" style={{ borderColor: accent }} />
        <div className="mt-0.5 border-t" style={{ borderColor: accent }} />
        <div className="mt-4 text-[15px] font-bold tracking-[0.3em]">{d.title}</div>
        {d.copyLabel && <div className="text-[10px] italic text-neutral-500">({d.copyLabel.toLowerCase()})</div>}
        <div className="mt-2 text-[11.5px]">{d.meta.map((m) => `${m.label} ${m.value}`).join("   ·   ")}</div>
      </div>
    );
  } else if (tpl.bold) {
    header = (
      <>
        <div className="flex flex-wrap items-center gap-5">
          <div className="text-[17px] font-bold">{d.title}</div>
          {d.copyLabel && <div className="rounded-md border-[1.5px] border-neutral-400 px-3.5 py-1.5 text-[13px] font-bold text-neutral-500">{d.copyLabel}</div>}
        </div>
        <div className="mt-4 flex items-center gap-5">
          {d.company.logo && (
            <div className="flex h-24 w-24 shrink-0 items-center justify-center bg-neutral-100 p-2">
              <img src={d.company.logo} alt="" className="max-h-full max-w-full object-contain" />
            </div>
          )}
          <div>
            <div className="text-[30px] leading-tight" style={{ color: accent }}>
              {d.company.name}
            </div>
            <Lines lines={d.company.lines} className="text-[15px] leading-relaxed text-neutral-900" />
          </div>
        </div>
        <div className="-mx-2 mt-2 h-2" style={{ background: accent }} />
        <div className="-mx-2 mt-2 flex flex-wrap gap-x-12 gap-y-1 bg-[#f4f5f7] px-4 py-3 text-[15px] font-bold">
          {d.meta.map((m) => (
            <span key={m.label}>
              {m.label} {m.value}
            </span>
          ))}
        </div>
      </>
    );
  } else {
    header = (
      <>
        <div className="flex items-start justify-between">
          <div className="text-[15px] font-bold tracking-wide">{d.title}</div>
          {copy}
        </div>
        <div className="mt-3 flex items-center gap-4">
          {d.company.logo && <img src={d.company.logo} alt="" className="h-14 w-14 object-contain" />}
          <div>
            <div className="text-[24px] font-bold leading-tight" style={{ color: accent }}>
              {d.company.name}
            </div>
            <Lines lines={d.company.lines} className="text-[11px] text-neutral-600" />
          </div>
        </div>
        <div className="mt-3 h-1.5" style={{ background: accent }} />
        <div className="flex flex-wrap gap-x-6 gap-y-1 bg-neutral-100 px-4 py-2.5 text-[11.5px]">
          {d.meta.map((m) => (
            <span key={m.label}>
              <b>{m.label}</b> {m.value}
            </span>
          ))}
        </div>
      </>
    );
  }

  const totalRowStyle = (s) => {
    if (s.tone !== "total") return undefined;
    if (tpl.totals === "band") return { background: accent, color: "#fff" };
    if (tpl.totals === "plain") return { color: accent, borderTop: "1.5px solid #1e1e1e" };
    return undefined;
  };
  const sumCell = tpl.totals === "boxed" ? "border border-neutral-200 px-2.5 py-2" : "px-2.5 py-1.5";

  return (
    <div
      className={
        thumbnail
          ? "paper-solid w-[794px] bg-white p-10 text-[12px] leading-snug text-neutral-900"
          : "paper-solid invoice-paper mx-auto w-full max-w-[820px] bg-white p-6 text-[12px] leading-snug text-neutral-900 rounded-sm shadow-lift ring-1 ring-slate-900/5 sm:p-10 print:ring-0 print:max-w-none print:p-0 print:shadow-none"
      }
      style={{ fontFamily: serif ? "'Times New Roman', Times, serif" : undefined }}
    >
      {header}

      {tpl.header !== "split" && tpl.header !== "boxed" && (
        <div className="mt-5 flex flex-wrap justify-between gap-4">
          <div className="flex gap-3">
            {clientLogo}
            <div className={tpl.bold ? "text-[15px] leading-relaxed" : undefined}>
              <div className={tpl.bold ? "font-bold" : "text-[11px] font-bold"}>{tpl.header === "minimal" ? "BILLED TO" : "BILL TO"}</div>
              <div className="mt-0.5 font-bold">{d.billTo.name}</div>
              <Lines lines={d.billTo.lines} className={tpl.bold ? "text-neutral-900" : "text-neutral-700"} />
            </div>
          </div>
          {d.extraMeta.length > 0 && (
            <table className="h-fit text-[11.5px]">
              <tbody>
                {d.extraMeta.map((m) => (
                  <tr key={m.label}>
                    <td className="pr-6 font-bold">{m.label}</td>
                    <td className="text-right text-neutral-700">{m.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {d.subject && (
        <div className={`mt-4 ${tpl.bold ? "text-[15px]" : "text-[12px]"}`}>
          <b>Subject:</b> {d.subject}
        </div>
      )}

      <div className="mt-5 overflow-x-auto">
        <table className={`w-full border-collapse ${tpl.bold ? "text-[15px]" : dense ? "text-[10.5px]" : "text-[11.5px]"}`}>
          <thead>
            <tr style={headStyle}>
              <th className={th("w-10 text-left")}>S.R</th>
              <th className={th("text-left")}>{tpl.header === "minimal" || tpl.header === "centered" ? "DESCRIPTION" : "SERVICE"}</th>
              {d.showHsn && <th className={th("text-center")}>HSN/SAC</th>}
              <th className={th(tpl.bold ? "text-left" : "text-center")}>QTY</th>
              <th className={th(num)}>RATE</th>
              {d.showDiscount && <th className={th(num)}>DISC</th>}
              <th className={th(num)}>{d.taxHeader}</th>
              <th className={th(num)}>AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {d.rows.length === 0 && (
              <tr>
                <td colSpan={cols} className="border border-neutral-200 px-2 py-6 text-center text-neutral-400">
                  No line items yet
                </td>
              </tr>
            )}
            {d.rows.map((r, i) => {
              if (r.kind === "section")
                return (
                  <tr key={`s${i}`} className="break-inside-avoid">
                    <td colSpan={cols} className={`${bodyBorder} px-2 pb-1 pt-3 text-[0.92em] font-bold uppercase tracking-wide ${r.optional ? "text-neutral-500" : ""}`} style={r.optional ? undefined : { color: accent }}>
                      {r.label}
                    </td>
                  </tr>
                );
              if (r.kind === "sectionTotal")
                return (
                  <tr key={`t${i}`} className={`break-inside-avoid font-semibold ${r.optional ? "italic text-neutral-500" : ""}`}>
                    <td colSpan={cols - 1} className={`${bodyBorder} ${cell} text-right`}>
                      {r.label}
                    </td>
                    <td className={`${bodyBorder} ${cell} ${num}`}>{r.amount}</td>
                  </tr>
                );
              return (
                <tr
                  key={`r${i}`}
                  className={`break-inside-avoid ${r.optional ? "text-neutral-500" : ""}`}
                  style={tpl.stripe && r.sr % 2 === 0 ? { background: tpl.head === "tint" ? tint(accent, 0.95) : "#f8f8f8" } : undefined}
                >
                  <td className={`${bodyBorder} ${cell}`}>{r.sr}</td>
                  <td className={`${bodyBorder} ${cell}`}>
                    <div className="flex gap-2.5">
                      {r.image && <img src={r.image} alt="" className="h-12 w-12 shrink-0 rounded object-cover ring-1 ring-neutral-200" />}
                      <div>
                        {r.description}
                        {r.optional && <span className="ml-1.5 rounded border border-neutral-300 px-1 text-[0.8em] uppercase">Optional</span>}
                        {r.details && <div className="mt-0.5 whitespace-pre-line text-[0.88em] leading-snug text-neutral-500">{r.details}</div>}
                      </div>
                    </div>
                  </td>
                  {d.showHsn && <td className={`${bodyBorder} ${cell} text-center`}>{r.hsn}</td>}
                  <td className={`${bodyBorder} ${cell} ${tpl.bold ? "" : "text-center"}`}>{r.qty}</td>
                  <td className={`${bodyBorder} ${cell} ${num}`}>{r.rate}</td>
                  {d.showDiscount && <td className={`${bodyBorder} ${cell} ${num}`}>{r.discount}</td>}
                  <td className={`${bodyBorder} ${cell} ${num}`}>{r.tax}</td>
                  <td className={`${bodyBorder} ${cell} ${num}`}>{r.amount}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="font-bold" style={footStyle}>
              <td colSpan={cols - 1} className="px-2 py-2">
                SUBTOTAL
              </td>
              <td className={`px-2 py-2 ${num}`}>{d.subtotal}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="mt-5 flex flex-col gap-5 break-inside-avoid sm:flex-row sm:justify-between">
        <div className={`sm:w-1/2 ${tpl.bold ? "text-[14px] leading-relaxed" : ""}`}>
          {d.bank.length > 0 && (
            <>
              <div className={tpl.bold ? "font-bold" : "text-[11px] font-bold"}>{tpl.header === "minimal" ? "PAYMENT DETAILS" : "BANK DETAILS"}</div>
              {d.bank.map(([k, v]) => (
                <div key={k} className={tpl.bold ? "text-neutral-900" : "text-neutral-700"}>
                  {k}: {v}
                </div>
              ))}
            </>
          )}
          {qr && (
            <div className="mt-2 w-24 text-center text-[10px] text-neutral-500">
              <img src={qr} alt="Payment QR" className="h-24 w-24" />
              Scan to pay
            </div>
          )}
        </div>
        <table className={`h-fit w-full border-collapse sm:w-[45%] ${tpl.bold ? "text-[14px]" : "text-[11.5px]"}`}>
          <tbody>
            {d.summary.map((s) => (
              <tr
                key={s.label}
                className={s.tone === "total" ? `font-bold ${tpl.bold ? "text-[16px]" : ""}` : s.tone === "credit" ? "font-bold text-green-700" : s.tone === "muted" ? "text-neutral-500" : ""}
                style={totalRowStyle(s)}
              >
                <td className={sumCell}>{s.label}</td>
                <td className={`${sumCell} ${tpl.totals === "boxed" ? "" : "text-right"}`}>{s.value}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={2} className={`${sumCell} font-bold`}>
                Total Amount (in words)
              </td>
            </tr>
            <tr>
              <td colSpan={2} className={`${sumCell} ${tpl.totals === "boxed" ? "font-bold" : "italic text-neutral-600"}`}>
                {d.words}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {d.schedule.length > 0 && (
        <div className="mt-5 break-inside-avoid">
          <div className={tpl.bold ? "text-[14px] font-bold" : "text-[11px] font-bold"}>PAYMENT SCHEDULE</div>
          <table className={`mt-1 w-full border-collapse sm:w-2/3 ${tpl.bold ? "text-[13px]" : "text-[11px]"}`}>
            <tbody>
              {d.schedule.map((x) => (
                <tr key={x.sr}>
                  <td className="w-8 border-b border-neutral-200 py-1 text-neutral-500">{x.sr}</td>
                  <td className="border-b border-neutral-200 py-1">{x.label}</td>
                  <td className="w-16 border-b border-neutral-200 py-1 text-right text-neutral-600">{x.percent}</td>
                  <td className="w-32 border-b border-neutral-200 py-1 text-right font-semibold">{x.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {d.footNotes.map((n) => (
        <div key={n} className="mt-4 italic text-neutral-600">
          {n}
        </div>
      ))}
      {d.notes && (
        <div className="mt-4">
          <div className="text-[11px] font-bold">NOTES</div>
          <div className="whitespace-pre-line text-neutral-700">{d.notes}</div>
        </div>
      )}
      {d.terms && (
        <div className="mt-4">
          <div className="text-[11px] font-bold">TERMS &amp; CONDITIONS</div>
          <div className="whitespace-pre-line text-neutral-700">{d.terms}</div>
        </div>
      )}

      <div className="mt-8 flex justify-end break-inside-avoid">
        {d.company.stamp && <img src={d.company.stamp} alt="Company stamp" className="mr-3 h-[100px] w-[100px] self-end object-contain" data-testid="doc-stamp" />}
        <div className="w-56 text-right">
          <div className="font-bold">For {d.company.name}</div>
          {d.company.signature ? <img src={d.company.signature} alt="Signature" className="ml-auto mt-2 h-14 object-contain" /> : <div className="h-10" />}
          <div className="border-t border-neutral-300 pt-1 text-neutral-600">{d.company.signatory}</div>
        </div>
      </div>
    </div>
  );
}
